import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { makeRequestStateless } from '@utils/helpers';
import { getCaseCountsReq } from './requests';

/**
 * How many cases wait in each work queue, for the bubbles on the sidebar
 * and the doctor's on-hold alert.
 *
 * Fetched once here and shared, rather than by each consumer: the rail
 * and the mobile drawer both draw the menu, and the alert needs the same
 * answer. Refreshed on a timer so a case that arrives while someone sits
 * on one page still shows up, on every navigation so acting on a case
 * updates its bubble straight away, and when the tab regains focus.
 */
const POLL_MS = 60 * 1000;

const EMPTY = { counts: {}, onhold_cases: [], refresh: () => {} };

const CaseCountsContext = createContext(EMPTY);

export const useCaseCounts = () => useContext(CaseCountsContext);

export const CaseCountsProvider = ({ children }) => {
  const [data, setData] = useState({ counts: {}, onhold_cases: [] });
  const location = useLocation();

  const refresh = useCallback(() => {
    // A failed poll keeps the last known numbers rather than blanking
    // every bubble.
    makeRequestStateless(getCaseCountsReq, null, (res) => {
      setData({
        counts: (res && res.counts) || {},
        onhold_cases: (res && Array.isArray(res.onhold_cases)) ? res.onhold_cases : [],
      });
    }, Function);
  }, []);

  useEffect(() => {
    refresh();
  }, [location.pathname, refresh]);

  useEffect(() => {
    const timer = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  // `refresh` lets a page that just changed what counts - opening a case
  // marks it read - update the bubbles straight away.
  const value = useMemo(() => ({ ...data, refresh }), [data, refresh]);

  return (
    <CaseCountsContext.Provider value={ value }>
      { children }
    </CaseCountsContext.Provider>
  );
};
